import {
  TaskViewCopyDTO,
  TaskViewCreateDTO,
  TaskViewDTO,
  TaskViewLayoutDTO,
  TaskViewLayoutUpdateDTO,
  TaskViewUpdateDTO,
} from 'models/dto/tasks/taskViewDTO';
import AppError from 'utils/appError';
import { getTaskViewLayoutSummary, logTaskViewDiagnostic } from 'utils/taskViewDiagnostics';
import http from './httpService';

const latestRevisionHeader = 'x-latest-revision-id';

type AppErrorWithLatestRevision = AppError & { latestRevision?: string };

export const getLatestRevisionFromError = (err: unknown): string | undefined => {
  const response = (err as { response?: { headers?: Record<string, string | string[] | undefined> } })?.response;
  const value = response?.headers?.[latestRevisionHeader];

  if (!value) return (err as { latestRevision?: string })?.latestRevision;

  return Array.isArray(value) ? value.join(',') : String(value);
};

export async function apiGetTaskViews(accessToken: string): Promise<TaskViewDTO[]> {
  logTaskViewDiagnostic('api.views.owned.get.start');
  try {
    //task views are the signed-in user's own saved views and drive the partner's sidebar; they must not be
    //replaced by the customer's views while a customer dashboard is open
    const ar = await http.get<TaskViewDTO[]>('/taskviews', http.getRequestConfig(accessToken, undefined, true));

    const views = Array.isArray(ar.data) ? ar.data : [];
    logTaskViewDiagnostic('api.views.owned.get.success', {
      count: views.length,
      viewKeys: views.map((view) => `view-api-${view.taskViewId}`),
    });

    return views;
  } catch (err) {
    logTaskViewDiagnostic('api.views.owned.get.error', {
      code: (err as { code?: string })?.code,
      status: (err as { response?: { status?: number } })?.response?.status,
    });

    throw AppError.fromApiError(err);
  }
}

export async function apiGetSharedTaskViews(accessToken: string): Promise<TaskViewDTO[]> {
  logTaskViewDiagnostic('api.views.shared.get.start');
  try {
    const ar = await http.get<TaskViewDTO[]>(
      '/taskviews/shared',
      http.getRequestConfig(accessToken, undefined, true),
    );

    const views = Array.isArray(ar.data) ? ar.data : [];
    logTaskViewDiagnostic('api.views.shared.get.success', {
      count: views.length,
      viewKeys: views.map((view) => `view-api-${view.taskViewId}`),
    });

    return views;
  } catch (err) {
    logTaskViewDiagnostic('api.views.shared.get.error', {
      code: (err as { code?: string })?.code,
      status: (err as { response?: { status?: number } })?.response?.status,
    });

    throw AppError.fromApiError(err);
  }
}

//Every call in this service passes ignoreCustomer: a task view is the signed-in user's own saved view, so it is
//never customer data whichever screen is open. The reads are load bearing (globalDataCache.refresh would otherwise
//replace the partner's sidebar with the customer's); the writes below are not reachable under a customer scope
//today, since only the Tasks scene calls them and it cannot be mounted at the same time as a customer dashboard -
//they are opted out anyway so the reasoning does not have to be redone the next time something moves.
export async function apiCreateTaskView(dto: TaskViewCreateDTO, accessToken: string): Promise<TaskViewDTO> {
  try {
    const ar = await http.post<TaskViewDTO>('/taskviews', dto, http.getRequestConfig(accessToken, undefined, true));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// Server-side copy of a view or a template; the API picks the first free
// "Name (n)" against its own uniqueness scope (own + all org-shared views).
export async function apiCopyTaskView(dto: TaskViewCopyDTO, accessToken: string): Promise<TaskViewDTO> {
  try {
    const ar = await http.post<TaskViewDTO>(
      '/taskviews/copy',
      dto,
      http.getRequestConfig(accessToken, undefined, true),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTaskView(id: number, dto: TaskViewUpdateDTO, accessToken: string): Promise<TaskViewDTO> {
  try {
    const ar = await http.put<TaskViewDTO>(
      `/taskviews/${id}`,
      dto,
      http.getRequestConfig(accessToken, undefined, true),
    );

    return ar.data;
  } catch (err) {
    // Optimistic-concurrency 409: keep the X-Latest-Revision-Id header on the
    // error for diagnostics, since AppError drops the axios response. The
    // retry itself reacts to code 409 and fetches the current row.
    const appError = AppError.fromApiError(err) as AppErrorWithLatestRevision;
    appError.latestRevision = getLatestRevisionFromError(err);

    throw appError;
  }
}

export async function apiDeleteTaskView(id: number, accessToken: string): Promise<void> {
  try {
    await http.delete(`/taskviews/${id}`, http.getRequestConfig(accessToken, undefined, true));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiShareTaskView(
  id: number,
  isShared: boolean,
  revision: string,
  accessToken: string,
): Promise<TaskViewDTO> {
  try {
    const shareDTO: Pick<TaskViewDTO, 'isShared' | 'revision'> = { isShared, revision };
    const ar = await http.put<TaskViewDTO>(
      `/taskviews/${id}/share`,
      shareDTO,
      http.getRequestConfig(accessToken, undefined, true),
    );

    return ar.data;
  } catch (err) {
    const appError = AppError.fromApiError(err) as AppErrorWithLatestRevision;
    appError.latestRevision = getLatestRevisionFromError(err);

    throw appError;
  }
}

export async function apiGetTaskViewLayout(accessToken: string): Promise<TaskViewLayoutDTO> {
  logTaskViewDiagnostic('api.layout.get.start');
  try {
    const ar = await http.get<TaskViewLayoutDTO>(
      '/taskviews/layout',
      http.getRequestConfig(accessToken, undefined, true),
    );

    logTaskViewDiagnostic('api.layout.get.success', {
      layout: getTaskViewLayoutSummary(ar.data),
    });

    return ar.data;
  } catch (err) {
    logTaskViewDiagnostic('api.layout.get.error', {
      code: (err as { code?: string })?.code,
      status: (err as { response?: { status?: number } })?.response?.status,
    });

    throw AppError.fromApiError(err);
  }
}

export async function apiSaveTaskViewLayout(
  layout: TaskViewLayoutDTO,
  accessToken: string,
): Promise<TaskViewLayoutDTO> {
  logTaskViewDiagnostic('api.layout.put.start', {
    layout: getTaskViewLayoutSummary(layout),
  });
  try {
    const dto: TaskViewLayoutUpdateDTO = { layout };
    //the sidebar layout belongs to the signed-in user at the partner; a debounced save must never write it
    //into the customer tenant
    const ar = await http.put<TaskViewLayoutDTO>(
      '/taskviews/layout',
      dto,
      http.getRequestConfig(accessToken, undefined, true),
    );

    logTaskViewDiagnostic('api.layout.put.success', {
      layout: getTaskViewLayoutSummary(ar.data),
    });

    return ar.data;
  } catch (err) {
    logTaskViewDiagnostic('api.layout.put.error', {
      code: (err as { code?: string })?.code,
      status: (err as { response?: { status?: number } })?.response?.status,
      latestRevision: getLatestRevisionFromError(err),
    });

    const appError = AppError.fromApiError(err) as AppErrorWithLatestRevision;
    appError.latestRevision = getLatestRevisionFromError(err);

    throw appError;
  }
}
